import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../ui/Button';
import { TextArea, TextField } from '../ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CONTACT_EMAIL } from '../../data/site';
import { trackEvent } from '../../utils/analytics';
import { MAX_IMAGE_BYTES, validateImage } from '../../services/imageService';
import { telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
const emptyForm = {
  name: '',
  phone: '',
  email: '',
  address: '',
  description: ''
};
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
interface Selected {
  file: File;
  preview: string;
  /** Udfyldt hvis filen ikke kan bruges — blokerer indsendelse indtil den fjernes. */
  problem: string | null;
}
export function ContactSection() {
  const {
    createEnquiry,
    settings
  } = useAdminData();
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState<Selected[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  /** Id på en påbegyndt, ufærdig henvendelse — genbruges ved nyt forsøg. */
  const [draftId, setDraftId] = useState<string | undefined>(undefined);
  const invalid = selected.filter(item => item.problem);
  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Skriv dit navn.';
    if (form.phone.replace(/[^\d]/g, '').length < 8) next.phone = 'Skriv et gyldigt telefonnummer.';
    if (form.email && !emailPattern.test(form.email.trim())) next.email = 'Skriv en gyldig e-mailadresse.';
    if (!form.description.trim()) next.description = 'Beskriv kort opgaven.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function removeImage(index: number) {
    setSubmitError('');
    setSelected(current => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((_, i) => i !== index);
    });
  }
  return <section id="kontakt" className="scroll-mt-24" data-id="element-252" data-mp="31wext7t83a53">
      <div className="mx-auto max-w-content px-5 py-16 md:py-24 lg:px-8" data-id="element-253" data-mp="2x2kggb13b796">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-254" data-mp="3bs9kqf4hbrdo">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-255" data-mp="3bucm1vb316xp">
            <h2 className="font-display text-[clamp(2rem,5.5vw,3.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-256" data-mp="36sn9gb87ol7i">
              Fortæl om din opgave
            </h2>
            <p className="mt-5 max-w-md text-[17px] leading-relaxed text-ink-soft" data-id="element-257" data-mp="3ftv5snr63byy">
              Beskriv kort hvad du skal have lavet. Du må meget gerne vedhæfte billeder, så er det nemmere at
              vurdere opgaven.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-258" data-mp="1lhwxqp4lgm4n">
              {settings.phone && <LinkButton href={telHref(settings.phone)} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-259" data-mp="10dyvb78x7dx">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-260" data-mp="3scpa8gbq2yvg" />
                  {settings.phone}
                </LinkButton>}
              <LinkButton href={`mailto:${CONTACT_EMAIL}`} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-261" data-mp="3ec64xgyn66wz">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-262" data-mp="35du8uzf0agx5" />
                {CONTACT_EMAIL}
              </LinkButton>
            </div>
          </div>

          {sent ? <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.28,
          ease
        }} role="status" className="flex flex-col items-start rounded-card border border-line bg-white p-8 md:p-12" data-id="element-263" data-mp="13lxxpqwr392w">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-264" data-mp="dxagvid3eoac">
                <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-265" data-mp="8zh60c0uh63q" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl" data-id="element-266" data-mp="40ntomxanl55">
                Tak for din henvendelse.
              </h3>
              <p className="mt-3 text-[17px] text-ink-soft" data-id="element-267" data-mp="3ravm6icrv5rd">
                LYKKEVÆRK vender tilbage hurtigst muligt.
              </p>
              <Button variant="secondary" size="md" className="mt-8" onClick={() => setSent(false)} data-id="element-268" data-mp="3ma7wc00a4uyq">
                Send en opgave mere
              </Button>
            </motion.div> : <form noValidate className="rounded-card border border-line bg-white p-6 md:p-9" onSubmit={async event => {
          event.preventDefault();
          if (submitting) return;
          if (!validate()) return;
          if (invalid.length > 0) {
            setSubmitError(`Billedet “${invalid[0].file.name}” kunne ikke bruges. Vælg et andet billede eller fjern det.`);
            return;
          }
          setSubmitting(true);
          setSubmitError('');
          const result = await createEnquiry({
            ...form,
            files: selected.map(item => item.file),
            draftId
          });
          setSubmitting(false);
          if (!result.ok) {
            // Samme dokument genbruges ved næste forsøg, så der ikke opstår dubletter.
            setDraftId(result.draftId);
            setSubmitError(result.error ?? 'Henvendelsen kunne ikke sendes. Prøv igen, eller ring til os i stedet.');
            return;
          }

          // Sendes udelukkende når henvendelsen og alle billeder er gemt korrekt.
          void trackEvent('generate_lead', {
            lead_source: 'website_contact_form',
            form_name: 'contact_form'
          });
          selected.forEach(item => URL.revokeObjectURL(item.preview));
          setSelected([]);
          setForm(emptyForm);
          setErrors({});
          setDraftId(undefined);
          setSent(true);
        }} data-id="element-269" data-mp="3cg7jtd2o4nfz">
              <p className="mb-5 text-sm text-ink-muted" data-id="element-270" data-mp="9fjyjkxuawmj">
                Felter markeret med <span className="text-accent" data-id="element-271" data-mp="4dul5yn5a6va">*</span> skal udfyldes.
              </p>

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-272" data-mp="1kscpwumpxj22">
                <TextField id="navn" label="Navn" required autoComplete="name" placeholder="Dit navn" value={form.name} error={errors.name} onChange={event => setForm({
              ...form,
              name: event.target.value
            })} data-id="element-273" data-mp="1kuyv4nm4zmh4" />
                <TextField id="telefon" label="Telefonnummer" required type="tel" inputMode="tel" autoComplete="tel" placeholder="12 34 56 78" value={form.phone} error={errors.phone} onChange={event => setForm({
              ...form,
              phone: event.target.value
            })} data-id="element-274" data-mp="2oskh2v5sy6s2" />
                <TextField id="email" label="E-mail" type="email" autoComplete="email" placeholder="dig@mail.dk" value={form.email} error={errors.email} onChange={event => setForm({
              ...form,
              email: event.target.value
            })} data-id="element-275" data-mp="3cootu86q0r7o" />
                <TextField id="omraade" label="Adresse eller område" autoComplete="street-address" placeholder="Fx Hvidovre" value={form.address} onChange={event => setForm({
              ...form,
              address: event.target.value
            })} data-id="element-276" data-mp="20c9t6s4wbv48" />
              </div>

              <TextArea id="beskrivelse" label="Beskrivelse af opgaven" required className="mt-5" placeholder="Fortæl kort hvad der skal laves." value={form.description} error={errors.description} onChange={event => setForm({
            ...form,
            description: event.target.value
          })} data-id="element-277" data-mp="2o1catzzj5nyq" />

              <div className="mt-5" data-id="element-278" data-mp="12mevulaz1ke7">
                <p className="mb-1.5 text-sm font-medium text-ink" data-id="element-279" data-mp="ymegmyg1t563">Upload billeder</p>
                <label htmlFor="billeder" className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-280" data-mp="3aajbii19ish7">
                  <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-281" data-mp="3o27jqa8e4wsw" />
                  <span className="text-[15px] font-medium text-ink" data-id="element-282" data-mp="3t1k9b2qnchz6">Vælg billeder</span>
                  <span className="text-sm text-ink-muted" data-id="element-283" data-mp="1ty9xmayn1o4">
                    Valgfrit — billeder op til {Math.round(MAX_IMAGE_BYTES / (1024 * 1024))} MB
                  </span>
                  <input id="billeder" type="file" multiple accept="image/*" className="sr-only" disabled={submitting} onChange={event => {
                const input = event.target;
                const picked = Array.from(input.files ?? []).map(file => ({
                  file,
                  preview: URL.createObjectURL(file),
                  problem: validateImage(file)
                }));
                setSelected(current => [...current, ...picked]);
                setSubmitError('');
                input.value = '';
              }} data-id="element-284" data-mp="av5ul1kk03zq" />
                </label>

                {invalid.length > 0 && <p role="alert" className="mt-3 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-285" data-mp="3nb59oq0xp9fr">
                    <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-286" data-mp="vty5e2progw" />
                    <span data-id="element-287" data-mp="5wrj1yf3ywm3">
                      {invalid.map(item => <span key={item.file.name} className="block" data-id="element-288" data-mp="9xa8jltj3ql4">
                          Billedet “{item.file.name}” kunne ikke bruges. Vælg et andet billede eller fjern det.
                        </span>)}
                    </span>
                  </p>}

                {selected.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-289" data-mp="1lffwe2n3u8p0">
                    {selected.map((item, index) => <li key={`${item.file.name}-${index}`} className={`overflow-hidden rounded-xl border ${item.problem ? 'border-danger' : 'border-line'}`} data-id="element-290" data-mp="1gi5wft23aemx" data-mpi={index}>
                        <div className="relative" data-id="element-291" data-mp="24bp84jajan5u">
                          <img src={item.preview} alt={item.file.name} className="aspect-square w-full object-cover" data-id="element-292" data-mp="1zfgx9i3ddg4h" />
                          <button type="button" onClick={() => removeImage(index)} aria-label={`Fjern billede ${item.file.name}`} className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-293" data-mp="2s1rzex6mjp3e">
                            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-294" data-mp="6hbn696yqlqz" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 py-2" data-id="element-295" data-mp="bihgo98g6d1k">
                          <span className={`truncate text-xs ${item.problem ? 'text-danger' : 'text-ink-muted'}`} data-id="element-296" data-mp="3tnufnsmkxgcy">
                            {item.file.name}
                          </span>
                          <button type="button" onClick={() => removeImage(index)} className="shrink-0 text-xs font-medium text-accent hover:text-accent-deep" data-id="element-297" data-mp="1wu99ohjobuul">
                            Fjern
                          </button>
                        </div>
                      </li>)}
                  </ul>}
              </div>

              {Object.keys(errors).length > 0 && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-298" data-mp="2pnw4qnyqnlfg">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-299" data-mp="2ypvovp51v5mm" />
                  Tjek de markerede felter, før du sender.
                </p>}

              {submitError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-300" data-mp="14ce244cgycsn">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-301" data-mp="11rpqm3vqslov" />
                  {submitError}
                </p>}

              <Button type="submit" size="lg" className="mt-7 w-full text-lg" disabled={submitting || invalid.length > 0} data-id="element-302" data-mp="16ru91zg7nqhq">
                {submitting ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-303" data-mp="j1q5fs30vqqq" />
                    {selected.length > 0 ? 'Sender og uploader billeder …' : 'Sender …'}
                  </> : 'Send forespørgsel'}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
}